import {
  Box,
  Button,
  Center,
  Flex,
  IconButton,
  Spinner,
  Step,
  StepIndicator,
  StepNumber,
  Stepper,
  StepSeparator,
  StepStatus,
  StepTitle,
  Text,
} from "@chakra-ui/react";
import { XIcon as IoClose } from "@phosphor-icons/react";
import { FaCheck } from "react-icons/fa6";
import { formatUnits, type Chain } from "viem";

import BaseImage from "@components/BaseImage";
import ModalDrawer from "@components/ModalDrawer";
import SuccessErrorModalBodyTemplate from "@components/SuccessErrorModalBodyTemplate";
import { OFT_LOCAL_DECIMALS, oftBridgePolicy } from "@config/oftBridgeConfig";
import { WrapBridgeMappings } from "@constants/common";
import { TransactionType } from "@constants/types";
import useLayerZeroDelivery from "@hooks/bridge/useLayerZeroDelivery";
import type { BridgeSendStep } from "@hooks/bridge/useOftBridgeSend";
import { formatNumber } from "@lib/utils";

type BridgeTransactionModalProps = {
  isOpen: boolean;
  sourceChain: Chain;
  destinationChain: Chain;
  /**
   * Display names for the two chains. Sourced from the same TChainSelection
   * title the bridge card itself renders (e.g. "Arbitrum") rather than
   * `Chain.name` (e.g. "Arbitrum One") — otherwise the card and this modal
   * disagree on what to call the same chain.
   */
  sourceChainName: string;
  destinationChainName: string;
  amountLD: bigint;
  nativeFee: bigint;
  /** Snapshotted by the caller when the flow opens; never recomputed here. */
  needsApproval: boolean;
  /** Whether `onRetry` can actually resubmit anything right now. */
  canRetry: boolean;
  step: BridgeSendStep;
  onConfirm: () => void;
  onRetry: () => void;
  onClose: () => void;
};

const STEPS = [
  { title: "Approve rsETH in wallet" },
  { title: "Confirm bridge" },
];

const chainIcon = (chainId: number): string =>
  WrapBridgeMappings.ICONS[chainId] || WrapBridgeMappings.ICONS.placeholder;

// Same header close affordance as DepositModalV2's persistent layout: always
// visible, never the "hidden md:block" treatment used by the success/error
// templates elsewhere in the app.
const CloseButton = ({ onClose }: { onClose: () => void }) => (
  <IconButton
    icon={<IoClose size={16} color="#075A5A" weight="regular" />}
    aria-label="Close Icon"
    backgroundColor="transparent"
    _hover={{ backgroundColor: "transparent" }}
    onClick={onClose}
    border="1px solid #EEEEEF"
    borderRadius="16px"
    className="!w-8 !h-auto !aspect-square"
  />
);

// Mirrors DepositModalV2's "You pay" / "You get" summary pill exactly (bg,
// radius, padding, width, icon + two-line text stack) with the chain name
// standing in for the pay/get label so the user can tell source from
// destination while the amount + rsETH plays the role of the headline value.
const ChainPill = ({
  chain,
  name,
  amountText,
}: {
  chain: Chain;
  name: string;
  amountText: string;
}) => (
  <Flex
    gap="6px"
    justifyContent="center"
    alignItems="center"
    bg="#F4F4F7"
    borderRadius="16px"
    padding="24px"
    width="50%"
    minWidth="130px"
    maxWidth="250px"
  >
    <Box width="max-content">
      <BaseImage src={chainIcon(chain.id)} width={24} height={24} alt={name} />
    </Box>
    <Flex
      alignItems={{ base: "start", md: "center" }}
      direction="column"
      gap="2px"
      width="max-content"
    >
      <Text
        color="#4A6A6A"
        fontSize="10px"
        lineHeight="12px"
        fontWeight="400"
        align="left"
      >
        {name}
      </Text>
      <Text
        fontSize="16px"
        lineHeight="24px"
        color="#1A1A1A"
        fontWeight={500}
        width="max-content"
      >
        {amountText} rsETH
      </Text>
    </Flex>
  </Flex>
);

// Styled like DepositModalV2's secondary rows below the summary pills (e.g.
// the "1 hgETH = x ETH" exchange-rate row): small label/value pair.
const SecondaryRow = ({ label, value }: { label: string; value: string }) => (
  <Flex
    justifyContent="space-between"
    alignItems="center"
    width="100%"
    padding="0 4px"
    marginBottom="4px"
  >
    <Text fontSize="11px" lineHeight="14px" fontWeight={400} color="#4A6A6A">
      {label}
    </Text>
    <Text fontSize="11px" lineHeight="14px" fontWeight={500} color="#1A1A1A">
      {value}
    </Text>
  </Flex>
);

// Same Stepper as DepositModalV2: vertical orientation, 120px height, gap 0,
// size lg, StepNumber/Spinner/FaCheck per status. Unlike DepositModalV2 (whose
// deposit transaction is already in flight by the time the stepper renders),
// our stepper is also used as a PREVIEW on the review/idle screen before the
// user has clicked anything — so the active step must only spin when a wallet
// interaction is genuinely in progress (`inFlight`); otherwise it shows its
// step number like the incomplete steps do.
const BridgeStepper = ({
  index,
  inFlight,
}: {
  index: number;
  inFlight: boolean;
}) => (
  <Stepper
    orientation="vertical"
    index={index}
    height="120px"
    padding={{ base: "0", md: 0 }}
    gap={0}
    size="lg"
  >
    {STEPS.map((step) => (
      <Step key={step.title}>
        <StepIndicator background="white" borderColor="#B6DFDF">
          <StepStatus
            incomplete={<StepNumber />}
            active={
              inFlight ? <Spinner size="sm" color="#0089D2" /> : <StepNumber />
            }
            complete={<FaCheck />}
          />
        </StepIndicator>
        <Box flexShrink="0" pt="0.7rem">
          <StepTitle>
            <Text
              fontSize="14px"
              lineHeight="18px"
              fontWeight={400}
              color="#1A1A1A"
            >
              {step.title}
            </Text>
          </StepTitle>
        </Box>
        <StepSeparator />
      </Step>
    ))}
  </Stepper>
);

const BridgeTransactionModal = ({
  isOpen,
  sourceChain,
  destinationChain,
  sourceChainName,
  destinationChainName,
  amountLD,
  nativeFee,
  needsApproval,
  canRetry,
  step,
  onConfirm,
  onRetry,
  onClose,
}: BridgeTransactionModalProps) => {
  // Polls LayerZero for destination delivery. Only meaningful once a send has
  // a receipt, and it stops on its own at a terminal state.
  const delivery = useLayerZeroDelivery({
    txHash: step.kind === "done" ? step.receipt.transactionHash : undefined,
    enabled: step.kind === "done",
  });

  const amountText = formatNumber(
    Number(formatUnits(amountLD, OFT_LOCAL_DECIMALS)),
    false,
    4,
    true
  );
  const feeText = `${formatNumber(
    Number(formatUnits(nativeFee, sourceChain.nativeCurrency.decimals)),
    false,
    6,
    true
    // The fee is charged in the SOURCE chain's native token — Mantle bills MNT.
  )} ${sourceChain.nativeCurrency.symbol}`;

  // Once the transaction is complete (success or failure) swap to a wholly
  // different body, exactly as DepositModalV2 early-returns <TransactionModal
  // /> once `complete` is true instead of continuing to render the summary.
  if (step.kind === "done") {
    return (
      <ModalDrawer
        isOpen={isOpen}
        closeAlert={onClose}
        closeOnOverlayClick={false}
        maxH="75%"
        bodyProps={{ overflow: "hidden" }}
      >
        <Center
          padding={{ base: "0", md: "2.5rem 3rem" }}
          fontWeight="600"
          flexDirection="column"
          width="100%"
          minWidth={{ base: "auto", md: "550px" }}
          position="relative"
        >
          <Box position="absolute" top="1rem" right="1rem">
            <CloseButton onClose={onClose} />
          </Box>
          <Flex
            width="100%"
            marginTop="1rem"
            alignItems="center"
            justifyContent="center"
          >
            <SuccessErrorModalBodyTemplate
              transactionType={
                delivery.kind === "failed"
                  ? TransactionType.WARNING
                  : TransactionType.SUCCESS
              }
              modalTitle={
                delivery.kind === "delivered"
                  ? `Delivered to ${destinationChainName}`
                  : "Bridge initiated"
              }
              modalSubTitle={
                delivery.kind === "delivered"
                  ? `Your ${amountText} rsETH has arrived on ${destinationChainName}.`
                  : delivery.kind === "failed"
                  ? delivery.reason
                  : `Your ${amountText} rsETH is on its way to ${destinationChainName}.`
              }
              primaryBtnTxt={
                delivery.kind === "delivered" && delivery.destinationTxHash
                  ? `View on ${destinationChainName}`
                  : "Track on LayerZero"
              }
              onSubmitPrimary={() =>
                window.open(
                  delivery.kind === "delivered" && delivery.destinationTxHash
                    ? `${destinationChain.blockExplorers?.default.url}/tx/${delivery.destinationTxHash}`
                    : oftBridgePolicy.layerZeroScanTxUrl(
                        step.receipt.transactionHash
                      ),
                  "_blank"
                )
              }
              secondaryBtnTxt="View transaction"
              onSubmitSecondary={() =>
                window.open(
                  `${sourceChain.blockExplorers?.default.url}/tx/${step.receipt.transactionHash}`,
                  "_blank"
                )
              }
            />
          </Flex>
          {delivery.kind === "pending" && (
            <Flex
              gap="8px"
              alignItems="center"
              justifyContent="center"
              marginTop="0.75rem"
            >
              <Spinner size="sm" color="#0089D2" />
              <Text fontSize="12px" color="#4A6A6A">
                Confirming delivery on {destinationChainName} — usually 2-3
                minutes
              </Text>
            </Flex>
          )}
        </Center>
      </ModalDrawer>
    );
  }

  if (step.kind === "error") {
    return (
      <ModalDrawer
        isOpen={isOpen}
        closeAlert={onClose}
        closeOnOverlayClick={false}
        maxH="75%"
        bodyProps={{ overflow: "hidden" }}
      >
        <Center
          padding={{ base: "0", md: "2.5rem 3rem" }}
          fontWeight="600"
          flexDirection="column"
          width="100%"
          minWidth={{ base: "auto", md: "550px" }}
          position="relative"
        >
          <Box position="absolute" top="1rem" right="1rem">
            <CloseButton onClose={onClose} />
          </Box>
          <Flex
            width="100%"
            marginTop="1rem"
            alignItems="center"
            justifyContent="center"
          >
            {step.error.hasUserDenied ? (
              <SuccessErrorModalBodyTemplate
                transactionType={TransactionType.WARNING}
                modalTitle="Transaction denied"
                modalSubTitle={
                  canRetry
                    ? "User denied transaction signature. Please try again"
                    : "User denied transaction signature. Please close this and start again."
                }
                primaryBtnTxt={canRetry ? "Try again" : undefined}
                onSubmitPrimary={canRetry ? onRetry : undefined}
              />
            ) : (
              <SuccessErrorModalBodyTemplate
                transactionType={TransactionType.ERROR}
                modalTitle="Transaction Failed"
                modalSubTitle={
                  canRetry
                    ? step.error.promptError
                    : `${step.error.promptError} Please close this and start again.`
                }
                primaryBtnTxt={canRetry ? "Try again" : undefined}
                onSubmitPrimary={canRetry ? onRetry : undefined}
              />
            )}
          </Flex>
        </Center>
      </ModalDrawer>
    );
  }

  // idle | review | approving | sending: ONE persistent layout. The summary
  // pills stay on screen the whole time — only the bottom action region
  // changes, matching DepositModalV2 exactly.
  const bottomRegion = (() => {
    if (step.kind === "approving") {
      return <BridgeStepper index={0} inFlight />;
    }

    if (step.kind === "sending") {
      if (needsApproval) {
        return <BridgeStepper index={1} inFlight />;
      }
      return (
        <Flex gap="12px" alignItems="center">
          <Flex
            alignItems="center"
            justifyContent="center"
            border="1px solid #6E9BDE"
            borderRadius="50%"
            padding="8px"
          >
            <Spinner size="sm" color="#0089D2" />
          </Flex>
          <Text color="#075A5A">Confirming bridge</Text>
        </Flex>
      );
    }

    // idle | review — the flow hasn't started yet, so (unlike DepositModalV2,
    // whose deposit already starts before the modal opens) we still need a
    // button here to trigger onConfirm. When approval is needed the stepper
    // previews the two upcoming steps above it.
    return (
      <>
        {needsApproval && <BridgeStepper index={0} inFlight={false} />}
        <Button
          width="12rem"
          backgroundColor="#075A5A"
          color="white"
          _hover={{ backgroundColor: "#075A5A", color: "white" }}
          alignSelf="center"
          borderRadius="0.5rem"
          onClick={onConfirm}
        >
          {needsApproval ? "Approve & Bridge" : "Confirm bridge"}
        </Button>
      </>
    );
  })();

  return (
    <ModalDrawer
      isOpen={isOpen}
      closeAlert={onClose}
      closeOnOverlayClick={false}
      maxH="75%"
      bodyProps={{ overflow: "hidden" }}
    >
      <Flex
        direction="column"
        gap="24px"
        padding="24px 32px"
        width={{ base: "100%", md: "548px" }}
        maxWidth={{ base: "100%", md: "548px" }}
      >
        <Flex alignItems="center" justifyContent="space-between">
          <Text
            fontSize="16px"
            lineHeight="24px"
            fontWeight={500}
            color="#1A1A1A"
          >
            Review bridge
          </Text>
          <CloseButton onClose={onClose} />
        </Flex>

        <Box padding="0" borderRadius="1rem">
          <Flex
            justifyContent="center"
            flexDirection="column"
            alignItems="center"
            gap="8px"
            width="100%"
          >
            <Flex
              justifyContent="space-between"
              alignItems={{ base: "start", md: "center" }}
              gap={{ base: "0.5rem", md: "1rem" }}
              direction={{ base: "row", md: "row" }}
              width="100%"
            >
              <ChainPill
                chain={sourceChain}
                name={sourceChainName}
                amountText={amountText}
              />
              <ChainPill
                chain={destinationChain}
                name={destinationChainName}
                amountText={amountText}
              />
            </Flex>

            <SecondaryRow label="Bridge fee" value={feeText} />
            <SecondaryRow label="Est. time to bridge" value="~10 Mins" />
          </Flex>
        </Box>

        {bottomRegion}
      </Flex>
    </ModalDrawer>
  );
};

export default BridgeTransactionModal;
